import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { reportsGateway } from '../../reports';
import { formatReportCurrency, formatReportDate } from '../../reports/formatting';
import type { PayrollRegisterResponse, PayrollRunSummary } from '../../reports/types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { ActiveFilterChips } from '../components/ActiveFilterChips';
import { downloadCsv } from '../csvExport';
import '../accounting-reports.css';

function runLabel(run: PayrollRunSummary): string {
  return `${formatReportDate(run.periodStartDate)} – ${formatReportDate(run.periodEndDate)} · Check date ${formatReportDate(run.checkDate)}`;
}

/** EPN-77 Payroll Journal report. Reads the exact same `getPayrollRegister` projection EPN-76's Payroll
 * Details page already uses — so this report's totals reconcile with Payroll Details/Payroll Register by
 * construction (same response, not a second computation). Employer Contributions, Reimbursements,
 * Department, and Work Location are not in this backend response today (confirmed: Department only lives
 * on the Employee record and is never propagated into payroll data; Work Location does not exist anywhere
 * on `main` yet) — those columns are shown as "Not tracked" rather than fabricated or silently omitted. */
export function PayrollJournalReportPage() {
  const [runs, setRuns] = useState<PayrollRunSummary[]>([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const [register, setRegister] = useState<PayrollRegisterResponse | null>(null);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    reportsGateway
      .listApprovedPayrollRuns(controller.signal)
      .then((result) => {
        const sorted = [...result].sort((a, b) => b.checkDate.localeCompare(a.checkDate));
        setRuns(sorted);
        setSelectedRunId(sorted[0]?.id ?? null);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setRunsError(friendlyErrorMessage(err, 'Could not load approved payroll runs.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setRunsLoading(false);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!selectedRunId) return;
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRegisterLoading(true);
    setRegisterError(null);
    setRegister(null);
    reportsGateway
      .getPayrollRegister(selectedRunId, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) setRegister(result);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setRegisterError(friendlyErrorMessage(err, 'Could not load the payroll journal for this run.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setRegisterLoading(false);
      });
    return () => controller.abort();
  }, [selectedRunId]);

  const selectedRun = runs.find((r) => r.id === selectedRunId) ?? null;
  const totalPayrollCost = register ? register.totals.grossEarnings + register.totals.employerTaxes : 0;

  const chips = selectedRun
    ? [{ key: 'run', label: `Payroll run: ${formatReportDate(selectedRun.checkDate)}`, onRemove: () => setSelectedRunId(runs[0]?.id ?? null) }]
    : [];

  function exportCsv() {
    if (!register) return;
    downloadCsv(
      `payroll-journal-${register.payrollRunId}.csv`,
      ['Employee Number', 'Employee Name', 'Gross Pay', 'Employee Taxes', 'Deductions', 'Net Pay', 'Employer Taxes', 'Total Payroll Cost'],
      register.lines.map((line) => [
        line.employeeNumber, line.employeeName, line.grossEarnings.toFixed(2), line.employeeTaxes.toFixed(2),
        (line.preTaxDeductionsTotal + line.postTaxDeductionsTotal + line.garnishmentsTotal).toFixed(2),
        line.netPay.toFixed(2), line.employerTaxes.toFixed(2), (line.grossEarnings + line.employerTaxes).toFixed(2),
      ]),
    );
  }

  return (
    <section className="card card--wide" aria-labelledby="payroll-journal-heading">
      <div className="section-header">
        <h2 id="payroll-journal-heading">Payroll Journal</h2>
        <button type="button" onClick={exportCsv} disabled={!register}>Export CSV</button>
      </div>

      {runsLoading && <p className="muted" role="status">Loading approved payroll runs…</p>}
      {runsError && <p className="error" role="alert">{runsError}</p>}
      {!runsLoading && !runsError && runs.length === 0 && (
        <p className="report-empty-state">No approved payroll runs yet. The Payroll Journal is available once a payroll run is approved.</p>
      )}

      {!runsLoading && runs.length > 0 && (
        <div className="report-filter-bar">
          <div className="report-filter-field">
            <label htmlFor="journal-run">Payroll run</label>
            <select id="journal-run" value={selectedRunId ?? ''} onChange={(e) => setSelectedRunId(e.target.value)}>
              {runs.map((r) => (
                <option key={r.id} value={r.id}>{runLabel(r)}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <ActiveFilterChips chips={chips} onClearAll={() => setSelectedRunId(runs[0]?.id ?? null)} />

      {registerLoading && <p className="muted" role="status">Loading payroll journal…</p>}
      {registerError && <p className="error" role="alert">{registerError}</p>}

      {register && selectedRun && (
        <>
          <dl aria-label="Pay period and check date">
            <div><dt>Pay period</dt><dd>{formatReportDate(register.periodStartDate)} – {formatReportDate(register.periodEndDate)}</dd></div>
            <div><dt>Pay date</dt><dd>{formatReportDate(register.checkDate)}</dd></div>
            <div><dt>Payroll status</dt><dd>{register.status}</dd></div>
          </dl>

          {register.lines.length === 0 ? (
            <p className="report-empty-state">No employee payroll journal lines for this payroll run.</p>
          ) : (
            <table aria-label="Payroll journal by employee">
              <thead>
                <tr>
                  <th scope="col">Employee</th>
                  <th scope="col">Department</th>
                  <th scope="col">Gross Pay</th>
                  <th scope="col">Employee Taxes</th>
                  <th scope="col">Deductions</th>
                  <th scope="col">Net Pay</th>
                  <th scope="col">Employer Taxes</th>
                  <th scope="col">Total Payroll Cost</th>
                  <th scope="col">Action</th>
                </tr>
              </thead>
              <tbody>
                {register.lines.map((line) => (
                  <tr key={line.employeeId}>
                    <th scope="row">{line.employeeNumber} — {line.employeeName}</th>
                    <td><span className="report-not-tracked">Not tracked</span></td>
                    <td>{formatReportCurrency(line.grossEarnings)}</td>
                    <td>{formatReportCurrency(line.employeeTaxes)}</td>
                    <td>{formatReportCurrency(line.preTaxDeductionsTotal + line.postTaxDeductionsTotal + line.garnishmentsTotal)}</td>
                    <td>{formatReportCurrency(line.netPay)}</td>
                    <td>{formatReportCurrency(line.employerTaxes)}</td>
                    <td>{formatReportCurrency(line.grossEarnings + line.employerTaxes)}</td>
                    <td>
                      <Link to={`/reports/payroll-details?run=${register.payrollRunId}`}>View Payroll Details</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">Totals</th>
                  <td />
                  <td>{formatReportCurrency(register.totals.grossEarnings)}</td>
                  <td>{formatReportCurrency(register.totals.employeeTaxes)}</td>
                  <td>{formatReportCurrency(register.totals.preTaxDeductionsTotal + register.totals.postTaxDeductionsTotal + register.totals.garnishmentsTotal)}</td>
                  <td>{formatReportCurrency(register.totals.netPay)}</td>
                  <td>{formatReportCurrency(register.totals.employerTaxes)}</td>
                  <td>{formatReportCurrency(totalPayrollCost)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          )}

          <p className="muted">
            Employer Contributions and Reimbursements are not yet modeled anywhere in EPN and are omitted rather than estimated.
            Department is recorded per employee but not yet attached to payroll data, so it cannot be shown or filtered here yet.
          </p>
        </>
      )}
    </section>
  );
}
